/**
 * ComplexOperationToAction class is used as a base for complex operations such as Upload and PingDownload operations.
 * @author Branislav Bogdanovic
 */
import * as _ from 'lodash';
import uuid from 'uuid-random';
import { Action, ErrorCodes, Operation } from '../Types';

// <debug>
type EditorDataType = {
  complexOperationId: string,
  additionalData: {
    complexParentOperationId: string, // used for dispatcher editor operation grouping
    internalOperation: boolean
  },
  original: { [key: string]: any }
};

// </debug>

abstract class ComplexOperationToAction {
  /**
   * Current action name.
   */
  private _actionName: string;
  /**
   * Current dispatcher id.
   * @internalProperty
   */
  private _appDispatcherId: string;

  /**
   * Enables debugging of operation.
   */
  private _debug: boolean | string;
  /**
   * Enables logging of operation.
   */
  private _log: boolean | string | undefined;
  /**
   * Error statuses that should be handled
   * @internalProperty
   */
  private _handleErrorStatuses: ErrorCodes[] | string;
  // <debug>
  /**
   * Data relevant to Dispatcher Editor.
   * @internalProperty
   */
  private _editorData: EditorDataType;
  // </debug>
  /**
   * Sets initialize data.
   * @param o Operation.
   * @param appDispatcherId Current dispatcher id.
   */
  protected constructor(o: Operation, appDispatcherId: string) {
    let { actionName, debug = false, log } = o;
    this.actionName = actionName;
    this.appDispatcherId = appDispatcherId;
    this.debug = debug;
    this.log = log;
    // <debug>
    this.initializeEditorData();
    // </debug>
  }

  // <debug>
  /**
   * Sets editor original data.
   * @param data
   */
  public addOriginalDataToEditor = (data: { [key: string]: any }) => {
    _.assign(this.editorData.original, data);
  };

  /**
   * Initialize common editor data.
   */
  private initializeEditorData = () => {
    let complexOperationId = this.actionName + '_' + uuid(); // used for dispatcher editor grouping of complex
    // operations
    this.editorData = {
      complexOperationId, // used for dispatcher editor grouping of complex operations
      additionalData: {
        complexParentOperationId: complexOperationId, // used for dispatcher editor operation grouping
        internalOperation: true
      },
      original: {}
    };
  };
  // </debug>
  /**
   * @override
   */
  public getOperations = (): Action => {
    throw new Error('Implement getOperations method!');
  };

  /**
   * Gets additional data needed for Dispatcher Editor.
   * @dispatcherEditor
   */
  protected getEditorAdditionalData() {
    return {
      // <debug>
      ...this.editorData.additionalData
      // </debug>
    };
  }

  /**
   * Gets common data for start of complex operation.
   * @param o
   * @dispatcherEditor
   */
  public getComplexOperationStartCommonData(o: Operation): Operation {
    return {
      ...o,
      actionName: o.actionName,
      dispatcherEditor: true,
      dispatcherId: this.appDispatcherId,
      complexOperationId: this.editorData.complexOperationId
    } as any;
  }

  /**
   * End of complex operation.
   * @dispatcherEditor
   */
  protected getComplexOperationEnd(): Operation[] {
    // eslint-disable-next-line array-bracket-newline
    return [
      // <debug>
      {
        actionName: this.actionName,
        dispatcherEditor: true,
        dispatcherId: this.appDispatcherId,
        independent: true,
        type: 'endOfComplexOperation'
      } as any
      // </debug>
      // eslint-disable-next-line array-bracket-newline
    ];
  }

  /**
   * GETTERS AND SETTERS BELLOW
   */
  get actionName(): string {
    return this._actionName;
  }

  set actionName(value: string) {
    this._actionName = value;
  }

  get appDispatcherId(): string {
    return this._appDispatcherId;
  }

  set appDispatcherId(value: string) {
    this._appDispatcherId = value;
  }

  get debug(): boolean | string {
    return this._debug;
  }

  set debug(value: boolean | string) {
    this._debug = value;
  }

  get log(): boolean | string | undefined {
    return this._log;
  }

  set log(value: boolean | string | undefined) {
    this._log = value;
  }

  get handleErrorStatuses(): ErrorCodes[] | string {
    return this._handleErrorStatuses;
  }

  set handleErrorStatuses(value: ErrorCodes[] | string) {
    this._handleErrorStatuses = value;
  }

  // <debug>
  get editorData(): EditorDataType {
    return this._editorData;
  }

  set editorData(value: EditorDataType) {
    this._editorData = value;
  }

  // </debug>
}

export default ComplexOperationToAction;
